import { usePlayerActions } from "@utils/player/core/usePlayer";
import List from "@components/Collection/List";
import { getPlaylistSubtitle } from "@components/Collection/playlist/helpers";
import usePlaylistScreenData from "@components/Collection/playlist/usePlaylistScreenData";
import { useMe } from "@hooks/trpc/useMe";
import { usePlaylistSuspense } from "@hooks/trpc/usePlaylists";
import { handleArtistNavigation } from "@utils/index";
import { useCallback } from "react";
import { getCountryCode } from "@utils/region";
import { ValidTerritoryCode } from "@libs/utils";

type PlaylistProps = {
  id: string;
  songId?: string;
};

const Playlist = ({ id, songId }: PlaylistProps) => {
  const playerActions = usePlayerActions();

  const me = useMe();
  const {
    query: { data: user },
  } = me;

  const {
    query: { data: playlist, isFetching },
  } = usePlaylistSuspense(id);

  const { songs, isOwner, images } = usePlaylistScreenData(
    Playlist.name,
    playlist,
    songId,
  );
  const playFromCollection = playerActions.playFromCollection;

  const handleSelectSong = useCallback(
    (songId: string) => {
      playFromCollection({
        songId,
        collectionType: "playlist",
        collectionId: id,
        collectionName: playlist?.title,
        songs,
      });
    },
    [id, playFromCollection, playlist?.title, songs],
  );

  const countryCode =
    user?.plan?.countryId ??
    user?.countryCode ??
    (getCountryCode() as ValidTerritoryCode);

  const handlePressList = () => {
    return handleArtistNavigation({
      params: { isPlaylist: true, numberOfArtists: 0 },
    });
  };

  // Suspense queryFn now returns `PlaylistWithSongs | undefined` (post
  // Phase 2.3 — for app-only playlist ids that short-circuit to undefined).
  // The screen is only used for real server playlists, but TS can no longer
  // narrow that — guard explicitly.
  if (!playlist) return null;

  const subtitle = getPlaylistSubtitle(
    user?.name,
    countryCode,
    playlist.user.name,
    playlist.id,
  );

  const hasPrivateSong = playlist.hasPrivateSong;

  return (
    <List
      type="playlist"
      id={id}
      isFetching={isFetching}
      image={images && images.length > 0 ? images : undefined}
      title={playlist?.title}
      subtitle={subtitle}
      onPress={handlePressList}
      isOwner={isOwner}
      isPublic={playlist?.visibility === "public"}
      songs={songs || []}
      hasPrivateSong={hasPrivateSong}
      onSelectSong={handleSelectSong}
    />
  );
};

export default Playlist;
